import React, { useEffect, useState, useMemo } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faFileExcel, faPrint } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

import useTableSelection from './shared/useTableSelection';
import TableToolbar from './shared/TableToolbar';
import { exportToPrint, exportToExcel, exportToPDF } from './shared/exportTable';

const AllBids = ({ openEditDrawer, refreshTrigger, filter = 'all', onBidsLoaded }) => {
    const [bidsData, setBidsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const userType = localStorage.getItem("usertype"); 
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");
    const loggedInFullName = (localStorage.getItem("fname") + " " + localStorage.getItem("lname")).trim();

    const fetchBids = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_all_bids.php', {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                let fetchedBids = res.data.data;
                if (userType === 'administrator') {
                    // Admin sees all
                } 
                else if (userType === 'leader') {
                    let leadersGroupId = null;
                    for (const bid of fetchedBids) {
                        if (bid.assigned_users && Array.isArray(bid.assigned_users)) {
                            const found = bid.assigned_users.find(u => String(u.id) === String(loggedInUserId));
                            if (found && bid.group_id) {
                                leadersGroupId = bid.group_id;
                                break; 
                            }
                        }
                    }
                    if (leadersGroupId) {
                        fetchedBids = fetchedBids.filter(bid => bid.group_id === leadersGroupId);
                    } else {
                        fetchedBids = []; 
                    }
                } 
                else if (userType === 'member') {
                    fetchedBids = fetchedBids.filter(bid => 
                        bid.assigned_users && Array.isArray(bid.assigned_users) && 
                        bid.assigned_users.some(u => String(u.id) === String(loggedInUserId))
                    );
                }
                setBidsData(fetchedBids);
                if (onBidsLoaded) onBidsLoaded(fetchedBids);
            } else {
                setBidsData([]);
            }
        } catch (err) { 
            console.error(err); 
            setBidsData([]); 
        } finally { 
            setLoading(false); 
        }
    };

    useEffect(() => {
        fetchBids();
    }, [refreshTrigger]);

    // =============== DELETE FUNCTION ===============
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this bid?")) return;
        try {
            const token = localStorage.getItem("token");
            const res = await axios.post(
                'https://skcapi.btcjapan.co/delete_bid.php',
                { id },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (res.data.success) {
                alert("Bid deleted successfully");
                fetchBids(); // Refresh the list
            } else {
                alert(res.data.error || "Delete failed");
            }
        } catch (err) {
            alert("Error deleting bid");
            console.error(err);
        }
    };    

    // ==================== APPLY DROPDOWN FILTER + SEARCH ====================
    const filteredBids = useMemo(() => {
        // Step 1 — apply the dropdown filter from Bids.js
        let result = bidsData;

        if (filter === 'me') {
            result = result.filter(bid =>
                Array.isArray(bid.assigned_users) &&
                bid.assigned_users.some(u => String(u.id) === String(loggedInUserId))
            );
        } else if (filter === 'everyone') {
            result = result.filter(bid => bid.assigned_to_type === 'everyone');
        } else if (filter.startsWith('stock:')) {
            const chassis = filter.split(':')[1].toLowerCase();
            result = result.filter(bid =>
                (bid.vehicle_details || '').toLowerCase().includes(chassis)
            );
        } else if (filter.startsWith('reporter:')) {
            const reporter = filter.split(':')[1];
            result = result.filter(bid => (bid.reporter || '') === reporter);
        } else if (filter.startsWith('staff:')) {
            const userId = filter.split(':')[1];
            result = result.filter(bid =>
                Array.isArray(bid.assigned_users) &&
                bid.assigned_users.some(u => String(u.id) === String(userId))
            );
        }

        // Step 2 — apply search on top
        const term = searchTerm.toLowerCase();
        if (!term) return result;

        return result.filter(bid =>
            (bid.bid_region || "").toLowerCase().includes(term) ||
            (bid.bid_date || "").includes(term) ||
            (bid.bid_amount || "").toString().includes(term) ||
            (bid.vehicle_details || "").toLowerCase().includes(term) ||
            (bid.reporter || "").toLowerCase().includes(term)
        );
    }, [bidsData, filter, searchTerm, loggedInUserId]);

    // Formatting Helpers
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-GB');
    };

    const formatTime = (datetime) => {
        if (!datetime) return '';
        return new Date(datetime).toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getAvatar = (user) => {
        if (user.image) {
            return (
                <img
                    src={user.image}
                    alt={user.name}
                    title={`${user.name} (${user.usertype})`}
                    style={{
                        width: 32, height: 32, borderRadius: '50%', objectFit: 'cover',
                        border: '2px solid #fff', boxShadow: '0 0 0 1px #ddd'
                    }}
                    onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                    }}
                />
            );
        }
        return null;
    };

    const getInitialAvatar = (user) => {
        const colors = { administrator: '#dc3545', leader: '#007bff', manager: '#fd7e14', member: '#28a745' };
        return (
            <div
                title={`${user.name} (${user.usertype})`}
                style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: colors[user.usertype] || '#6c757d', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 600, border: '2px solid #fff',
                    boxShadow: '0 0 0 1px #ddd'
                }}
            >
                {user.name?.charAt(0).toUpperCase()}
            </div>
        );
    };

    const selection = useTableSelection(filteredBids, (row) => row.id);

    const exportColumns = [
        'Date',
        'Time',
        'Bid Amount',
        'Region',
        'Vehicle Details',
        'Trans',
        'Drive',
        'Reverse',
        'Shipment',
        'Units',
        'Assigned To',
    ];

    const formatAssignedTo = (bid) => {
        if (bid.assigned_to_type === 'everyone') return 'Everyone';
        if (!Array.isArray(bid.assigned_users) || bid.assigned_users.length === 0) return '';
        return bid.assigned_users
            .map(u => `${u.name}${u.usertype ? ` (${u.usertype})` : ''}`)
            .join(', ');
    };

    const buildExportRows = (rows) => rows.map(bid => [
        // Date — use the same formatted value that's visible in the table
        bid.bid_date
            ? new Date(bid.bid_date).toLocaleDateString('en-GB')
            : '',
        // Time — from created_at
        bid.created_at
            ? new Date(bid.created_at.replace(' ', 'T')).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
            : '',
        // Bid amount — formatted with thousand separators (matches the on-screen number)
        Number(bid.bid_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        bid.bid_region || '',
        // Vehicle details — multi-line; newlines are preserved by our exporters
        bid.vehicle_details || '',
        bid.transmission || '',
        bid.drive_type || '',
        bid.reverse_option || '',
        bid.shipment_type || '',
        bid.required_units || '',
        formatAssignedTo(bid),
    ]);

    const handleExport = (kind) => {
        const rows = selection.selectedRows;
        if (rows.length === 0) return;
        const title = 'Bids Export';
        const data = buildExportRows(rows);
        if (kind === 'print') exportToPrint(title, exportColumns, data);
        if (kind === 'excel') exportToExcel(title, exportColumns, data);
        if (kind === 'pdf')   exportToPDF(title, exportColumns, data);
    };

    return (
        <div className="bids-list-container">

            <TableToolbar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                isSelectMode={selection.isSelectMode}
                onToggleSelectMode={selection.toggleSelectMode}
                onSelectAll={() =>
                    selection.allSelected ? selection.clearSelection() : selection.selectAll()
                }
                selectedCount={selection.selectedIds.size}
                allSelected={selection.allSelected}
                onPrint={() => handleExport('print')}
                onExcel={() => handleExport('excel')}
                onPDF={() => handleExport('pdf')}
            />

            <div className="bids-table-wrapper" style={{ overflowX: 'auto', background: '#fff', borderRadius: 8 }}>
                <table className="bids-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                        <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                            {selection.isSelectMode && (
                                <th style={{ width: 42, textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={selection.allSelected}
                                        onChange={() =>
                                            selection.allSelected
                                                ? selection.clearSelection()
                                                : selection.selectAll()
                                        }
                                        title={selection.allSelected ? 'Unselect All' : 'Select All'}
                                    />
                                </th>
                            )}
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Date & Time</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Bid Amount</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Region</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left', minWidth: 280 }}>Vehicle Details</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Trans</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Drive</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Reverse</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Shipment</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Units</th>
                            <th style={{ padding: '12px 10px', textAlign: 'left' }}>Assigned To</th>
                            <th style={{ padding: '12px 10px', textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={selection.isSelectMode ? 13 : 12} style={{ textAlign: 'center', padding: 60, color: '#6c757d' }}>Loading bids...</td></tr>
                        ) : filteredBids.length === 0 ? (
                            <tr>
                                <td colSpan={selection.isSelectMode ? 13 : 12} style={{ textAlign: 'center', padding: 60, color: '#6c757d' }}>
                                    {bidsData.length === 0
                                        ? "No bids found."
                                        : "No bids match the current filter."}
                                </td>
                            </tr>
                        ) : (
                            filteredBids.map((bid) => (
                                <tr key={bid.id} style={{ borderBottom: '1px solid #eee' }}>
                                    {selection.isSelectMode && (
                                        <td
                                            style={{ textAlign: 'center' }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selection.isRowSelected(bid.id)}
                                                onChange={() => selection.toggleRow(bid.id)}
                                            />
                                        </td>
                                    )}
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>
                                        <div style={{ fontWeight: 500 }}>{formatDate(bid.bid_date)}</div>
                                        <div style={{ fontSize: 11, color: '#888' }}>{formatTime(bid.created_at)}</div>
                                    </td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top', fontWeight: 600 }}>
                                        {Number(bid.bid_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>{bid.bid_region}</td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top', maxWidth: 320 }}>
                                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                                            {bid.vehicle_details?.split('\n').map((line, i) => (
                                                <div key={i} style={{ marginBottom: 2 }}>{line.startsWith('•') || line.startsWith('-') ? line : `• ${line}`}</div>
                                            ))}
                                        </div>
                                    </td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>{bid.transmission}</td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>{bid.drive_type}</td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>{bid.reverse_option}</td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>{bid.shipment_type}</td>
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>{bid.required_units}</td>
                                    
                                    <td style={{ padding: '14px 10px', verticalAlign: 'top' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {bid.assigned_to_type === 'everyone' ? (
                                                <span style={{ background: '#6f42c1', color: '#fff', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>Everyone</span>
                                            ) : (
                                                <>
                                                    {bid.assigned_users?.slice(0, 4).map((user, idx) => (
                                                        <div key={idx} style={{ position: 'relative' }}>
                                                            {getAvatar(user)}
                                                            <div style={{ display: user.image ? 'none' : 'flex' }}>{getInitialAvatar(user)}</div>
                                                        </div>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    </td>

                                    <td>
                                        <div className="action-btns bid-btns" >
                                            <button className="action-icon-btn edit" onClick={() => openEditDrawer && openEditDrawer(bid.id)}>✎</button>
                                            <button className="action-icon-btn delete" onClick={() => handleDelete(bid.id)}>🗑</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
export default AllBids;